import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, MapPin, User, ChevronDown, ChevronUp, Edit } from 'lucide-react';
import AuthDebugger from './AuthDebugger';

const Calendar = ({ sessions = [], events = [], loading = false, dataSource = 'demo', account, isAuthenticated, childSessionIds = [], userRole = 'viewer' }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSession, setSelectedSession] = useState(null);
  const [locationFilter, setLocationFilter] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [campusFilter, setCampusFilter] = useState('all');
  const [instructorFilter, setInstructorFilter] = useState([]);
  const [classroomFilter, setClassroomFilter] = useState('all');
  const [resourcesFilter, setResourcesFilter] = useState('all');
  const [showDebug, setShowDebug] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [instructorDropdownOpen, setInstructorDropdownOpen] = useState(false);
  
  const locationDropdownRef = useRef(null);
  const instructorDropdownRef = useRef(null);

  // -----------------------------
  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setLocationDropdownOpen(false);
      }
      if (instructorDropdownRef.current && !instructorDropdownRef.current.contains(event.target)) {
        setInstructorDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sizing constants
  // -----------------------------
  const HEADER_H = 40;     // Header row height ("Sun Mon Tue...")
  const CELL_BASE = 96;    // Minimum height for each week row
  const EVENT_H = 24;      // Event band height
  const EVENT_GAP = 2;     // Vertical gap between event bands
  const GRID_GAP = 1;      // Grid row/column gap
  const GRID_PAD = 4;      // Grid outer padding
  const ROW_INSET = 28;    // Top/bottom inset within a week row (extra space for date numbers)

  // Department color mapping
  const departmentColors = {
    'Hawaii': '#22c55e',              // Green
    'OCC': '#ec4899',                 // Pink
    'Engineering': '#ef4444',         // Red
    'Deck': '#60a5fa',                // Light Blue
    'N/A': '#7f1d1d',                 // Dark Maroon/Red
    'Weapons': '#d2b48c',             // Tan
    'Everett': '#14b8a6',             // Turquoise
    'Edmonds': '#0d9488',             // Dark turquoise
    'NSW/Navy': '#1e3a8a',            // Dark Blue
    'Gov Vessel': '#78350f',          // Brown
    'NOAA': '#f97316',                // Orange
    'Special Course': '#fca5a5',      // Light red
    'Alameda': '#991b1b',             // Dark Red
    'Boot Camp': '#fbbf24',           // Yellow
    'Shipyard Boot Camp/Welding': '#78350f' // Brown
  };

  const getDepartmentColor = (item) => {
    const deptName = item.departmentName || '';
    const color = departmentColors[deptName] || '#3b82f6';
    if (!departmentColors[deptName] && deptName) {
      console.log(`⚠️ Unmapped department: "${deptName}" - using default blue`);
    }
    return color;
  };

  // ---------- Robust local-date parsing ----------
  // Returns a Date at local midnight (y, m, d) based on the *local* interpretation of the input
  const toLocalDate = (input) => {
    if (!input) return null;

    if (input instanceof Date) {
      return new Date(input.getFullYear(), input.getMonth(), input.getDate());
    }

    // Handle ISO date strings by parsing components directly to avoid UTC conversion issues
    const isoMatch = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return new Date(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]);
    }

    // Try general Date parsing as fallback
    const dt = new Date(input);
    if (!Number.isNaN(dt.getTime())) {
      // For parsed dates, use local timezone interpretation
      return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    }

    return null;
  };

  // Normalize start/end to local dates using the same parsing logic as toLocalDate
  const normalizeSpanRange = useCallback((session) => {
    const rawStart = session.mi_startdate;
    const rawEnd   = session.mi_enddate ?? session.mi_startdate;

    const startLocal = toLocalDate(rawStart);
    const endLocal = toLocalDate(rawEnd);
    
    if (!startLocal || !endLocal) return null;

    // Ensure end is not before start
    if (endLocal < startLocal) {
      return { startLocal, endLocal: startLocal };
    }

    return { startLocal, endLocal };
  }, []);

  // Calendar navigation
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Navigate to today
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Handle date picker change
  const handleDatePickerChange = (e) => {
    const selectedDate = new Date(e.target.value + 'T00:00:00');
    if (!isNaN(selectedDate.getTime())) {
      setCurrentDate(selectedDate);
    }
  };

  // Format current date for the date input (YYYY-MM-DD)
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Build the 6-week (42 days) calendar grid
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: prevMonth.getDate() - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, prevMonth.getDate() - i)
      });
    }

    // Current month days
    for (let date = 1; date <= daysInMonth; date++) {
      days.push({
        date,
        isCurrentMonth: true,
        fullDate: new Date(year, month, date)
      });
    }

    // Next month filler days (always 6 rows, 42 cells)
    const remaining = 42 - days.length;
    for (let date = 1; date <= remaining; date++) {
      days.push({
        date,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, date)
      });
    }

    return days;
  }, [currentDate]);

  const firstVisible = calendarData[0]?.fullDate;
  const lastVisible  = calendarData[calendarData.length - 1]?.fullDate;

  // Combine sessions and events for calendar display
  const allItems = useMemo(() => {
    return [...sessions, ...events];
  }, [sessions, events]);

  const getUniqueLocations = () => {
    const locations = allItems
      .map(session => ({
        value: session.locationName || '',
        name: String(session.locationName || 'Unknown Location')
      }))
      .filter(location => location.value && location.value !== '' && location.value !== 'Unknown Location' && location.value.toLowerCase() !== 'online')
      .reduce((unique, location) => {
        if (!unique.find(item => item.value === location.value)) {
          unique.push(location);
        }
        return unique;
      }, []);

    return locations.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getUniqueDepartments = () => {
    const departments = allItems
      .map(session => ({
        value: session.departmentName || String(session.cr38c_department || ''),
        name: String(session.departmentName || session.cr38c_department || 'Unknown Department')
      }))
      .filter(dept => dept.value && dept.value !== '' && dept.value !== 'Unknown Department')
      .reduce((unique, dept) => {
        if (!unique.find(item => item.value === dept.value)) {
          unique.push(dept);
        }
        return unique;
      }, []);

    return departments.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getUniqueCampuses = () => {
    const campuses = allItems
      .map(session => ({
        value: session.campusName || '',
        name: String(session.campusName || 'Unknown Campus')
      }))
      .filter(campus => campus.value && campus.value !== '' && campus.value !== 'Unknown Campus')
      .reduce((unique, campus) => {
        if (!unique.find(item => item.value === campus.value)) {
          unique.push(campus);
        }
        return unique;
      }, []);

    return campuses.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getUniqueClassrooms = () => {
    const classrooms = allItems
      .map(item => ({
        value: item.classroomName || '',
        name: String(item.classroomName || 'Unknown')
      }))
      .filter(c => c.value && c.value !== '' && c.value !== 'Unknown')
      .reduce((unique, c) => {
        if (!unique.find(item => item.value === c.value)) {
          unique.push(c);
        }
        return unique;
      }, []);

    return classrooms.sort((a, b) => a.name.localeCompare(b.name));
  };

  const getUniqueResources = () => {
    const resourceMap = new Map();
    
    allItems.forEach(item => {
      if (item.resources && item.resources.length > 0) {
        item.resources.forEach(resource => {
          if (resource.name && resource.name !== 'Unknown Resource') {
            resourceMap.set(resource.name, {
              value: resource.name,
              name: resource.name
            });
          }
        });
      }
    });

    return Array.from(resourceMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  const getUniqueInstructors = () => {
    const instructors = new Map();
    
    allItems.forEach(item => {
      // For sessions: Add parent session instructors
      if (!item.isEvent && item.instructors?.length > 0) {
        item.instructors.forEach(instructor => {
          if (instructor.name && instructor.name !== 'Unknown Instructor') {
            // Use instructor name as the key to deduplicate properly
            instructors.set(instructor.name, {
              value: instructor.name,
              name: String(instructor.name)
            });
          }
        });
      } else if (item.mi_instructor && item.mi_instructor !== 'TBD' && item.mi_instructor !== '') {
        // For both sessions and events: Add instructor by name
        instructors.set(item.mi_instructor, {
          value: item.mi_instructor,
          name: String(item.mi_instructor)
        });
      }
      
      // For sessions: Add child session instructors
      if (!item.isEvent && item.childSessions?.length > 0) {
        item.childSessions.forEach(child => {
          if (child.instructors?.length > 0) {
            child.instructors.forEach(instructor => {
              if (instructor.name && instructor.name !== 'Unknown Instructor') {
                instructors.set(instructor.name, {
                  value: instructor.name,
                  name: String(instructor.name)
                });
              }
            });
          } else if (child.mi_instructor && child.mi_instructor !== 'TBD') {
            instructors.set(child.mi_instructor, {
              value: child.mi_instructor,
              name: String(child.mi_instructor)
            });
          }
        });
      }
    });

    const sortedInstructors = Array.from(instructors.values()).sort((a, b) => a.name.localeCompare(b.name));
    
    // Add "TBD" option at the top for sessions with no instructor assigned
    return [{ value: '__TBD__', name: 'TBD (No Instructor)' }, ...sortedInstructors];
  };

  // Filtered sessions and events by all filters + strong de-dupe (ignores differing IDs)
  const filteredSessions = useMemo(() => {
    // Filter out child sessions and inactive items
    let base = allItems.filter(s => {
      // Hide child sessions
      if (childSessionIds.includes(s.mi_sessionid)) return false;
      
      // Hide inactive items (statecode 1 = Inactive)
      if (s.statecode === 1) return false;
      
      // Hide sessions with "Online" location
      const itemLocation = (s.locationName || String(s._mi_location_value || s.mi_location || '')).toLowerCase();
      if (itemLocation === 'online') return false;
      
      // Hide FCC EXAMINATIONS ELEMENT 1 sessions
      const sessionName = (s.mi_sessionname || '').toUpperCase();
      if (sessionName.includes('FCC EXAMINATIONS ELEMENT 1 MARINE RADIO OPERATOR\'S PERMIT')) return false;
      
      return true;
    });
    
    // Apply location filter (multi-select)
    if (locationFilter.length > 0) {
      base = base.filter(s => {
        const itemLocation = s.locationName || String(s._mi_location_value || s.mi_location || '');
        return locationFilter.includes(itemLocation);
      });
    }
    
    // Apply department filter (use formatted value)
    if (departmentFilter !== 'all') {
      base = base.filter(s => (s.departmentName || String(s.cr38c_department || '')) === departmentFilter);
    }
    
    // Apply campus filter (use formatted value)
    if (campusFilter !== 'all') {
      base = base.filter(s => (s.campusName || String(s.mi_billingresponsibility || '')) === campusFilter);
    }
    
    // Apply classroom filter
    if (classroomFilter !== 'all') {
      base = base.filter(s => {
        const classroom = s.classroomName || String(s.mi_classroom || '');
        return classroom === classroomFilter;
      });
    }
    
    // Apply resources filter
    if (resourcesFilter !== 'all') {
      base = base.filter(s => {
        if (s.resources && s.resources.length > 0) {
          return s.resources.some(resource => resource.name === resourcesFilter);
        }
        return false;
      });
    }
    
    // Apply instructor filter (multi-select, check parent, child sessions, and events)
    if (instructorFilter.length > 0) {
      base = base.filter(s => {
        // Check if TBD filter is selected (sessions with no instructor)
        const tbdSelected = instructorFilter.includes('__TBD__');
        const otherFilters = instructorFilter.filter(f => f !== '__TBD__');
        
        // Check if this session has no instructor assigned
        const hasNoInstructor = () => {
          const mainInstructor = s.mi_instructor;
          const hasMainInstructor = mainInstructor && mainInstructor !== 'TBD' && mainInstructor !== '';
          const hasInstructorList = !s.isEvent && s.instructors?.length > 0;
          return !hasMainInstructor && !hasInstructorList;
        };
        
        // If TBD is selected and this session has no instructor, include it
        if (tbdSelected && hasNoInstructor()) {
          return true;
        }
        
        // If no other filters selected (only TBD), we've already checked above
        if (otherFilters.length === 0) {
          return false;
        }
        
        // Check if any selected instructor matches
        let hasInstructor = false;
        
        // Check main instructor (works for both sessions and events)
        if (s.mi_instructor && otherFilters.includes(s.mi_instructor)) {
          hasInstructor = true;
        }
        
        // For sessions: check instructor list
        if (!hasInstructor && !s.isEvent && s.instructors?.length > 0) {
          hasInstructor = s.instructors.some(instructor => otherFilters.includes(instructor.name));
        }
        
        // For sessions: check child sessions
        if (!hasInstructor && !s.isEvent && s.childSessions?.length > 0) {
          hasInstructor = s.childSessions.some(child => {
            if (child.instructors?.length > 0) {
              return child.instructors.some(instructor => otherFilters.includes(instructor.name));
            }
            return child.mi_instructor && otherFilters.includes(child.mi_instructor);
          });
        }
        
        return hasInstructor;
      });
    }

    const seen = new Set();
    const out = [];

    for (const s of base) {
      const norm = normalizeSpanRange(s);
      // If we can't normalize, still allow through using raw dates to avoid hiding data
      const startKey = norm ? norm.startLocal.toISOString().slice(0,10) : (toLocalDate(s.mi_startdate)?.toISOString().slice(0,10) || s.mi_startdate || '');
      const endKey   = norm ? norm.endLocal.toISOString().slice(0,10)   : (toLocalDate(s.mi_enddate)?.toISOString().slice(0,10)   || s.mi_enddate   || '');

      const nameKey = (s.mi_sessionname || '').trim();
      const locKey  = (s._mi_location_value || s.mi_location || '').toString().trim();
      const idKey = s.mi_sessionid || s.cr38c_eventid || '';

      // Include session ID in key to ensure distinct sessions with same name/date/location are shown
      const key = `${idKey}|${nameKey}|${startKey}|${endKey}|${locKey}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s);
    }
    return out;
  }, [allItems, locationFilter, departmentFilter, campusFilter, classroomFilter, resourcesFilter, instructorFilter, childSessionIds, normalizeSpanRange]);



  // Calculate event spans across the 6x7 grid
  const getEventSpans = useMemo(() => {
    const spans = [];

    filteredSessions.forEach(session => {
      if (!session.mi_startdate) return;

      const norm = normalizeSpanRange(session);
      if (!norm) return;

      // Clamp to visible grid
      const startClamped = new Date(Math.max(norm.startLocal.getTime(), firstVisible.getTime()));
      const endClamped   = new Date(Math.min(norm.endLocal.getTime(),   lastVisible.getTime()));

      if (endClamped < startClamped) return;

      const startIndex = calendarData.findIndex(day =>
        day.fullDate.toDateString() === startClamped.toDateString()
      );
      const endIndex = calendarData.findIndex(day =>
        day.fullDate.toDateString() === endClamped.toDateString()
      );

      if (startIndex === -1 || endIndex === -1) return;

      const startRow = Math.floor(startIndex / 7);
      const endRow   = Math.floor(endIndex / 7);

      if (startRow === endRow) {
        spans.push({
          session,
          row: startRow,
          startCol: startIndex % 7,
          endCol: endIndex % 7,
          duration: endIndex - startIndex + 1,
          isFirstSegment: true,
          isLastSegment: true
        });
      } else {
        // Break into weekly segments (visual)
        for (let row = startRow; row <= endRow; row++) {
          const rowStartCol = row === startRow ? (startIndex % 7) : 0;
          const rowEndCol   = row === endRow   ? (endIndex % 7)   : 6;

          spans.push({
            session,
            row,
            startCol: rowStartCol,
            endCol: rowEndCol,
            duration: rowEndCol - rowStartCol + 1,
            isFirstSegment: row === startRow,
            isLastSegment: row === endRow
          });
        }
      }
    });

    // De-dupe spans defensively
    const seen = new Set();
    const unique = [];
    for (const sp of spans) {
      const idKey = sp.session.mi_sessionid || sp.session.cr38c_eventid || '';
      const nameKey = (sp.session.mi_sessionname || '').trim();
      const locKey  = (sp.session._mi_location_value || sp.session.mi_location || '').toString().trim();
      const key = `${idKey}|${nameKey}|${sp.row}|${sp.startCol}|${sp.endCol}|${locKey}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(sp);
    }
    return unique;
  }, [filteredSessions, calendarData, firstVisible, lastVisible, normalizeSpanRange]);

  // ---------- Lane packing to avoid vertical overlaps ----------
  const laidOut = useMemo(() => {
    const rows = Array.from({ length: 6 }, () => []);
    getEventSpans.forEach(s => rows[s.row].push(s));

    const placed = [];
    const rowMaxLanes = [];

    rows.forEach((rowSpans, rowIdx) => {
      rowSpans.sort((a, b) => (a.startCol - b.startCol) || (a.endCol - b.endCol));
      const lanes = []; // lanes[k] = last occupied endCol in lane k

      rowSpans.forEach(span => {
        let laneIndex = lanes.findIndex(lastEnd => lastEnd < span.startCol);
        if (laneIndex === -1) {
          laneIndex = lanes.length;
          lanes.push(span.endCol);
        } else {
          lanes[laneIndex] = span.endCol;
        }
        placed.push({ ...span, lane: laneIndex });
      });

      rowMaxLanes[rowIdx] = lanes.length || 1;
    });

    return { spans: placed, rowMaxLanes };
  }, [getEventSpans]);

  // ---------- Dynamic row heights ----------
  const rowHeights = useMemo(() => {
    const maxLanes = laidOut?.rowMaxLanes || Array(6).fill(1);
    return maxLanes.map(nLanes =>
      Math.max(
        CELL_BASE,
        ROW_INSET + (nLanes * EVENT_H) + ((nLanes - 1) * EVENT_GAP) + ROW_INSET
      )
    );
  }, [laidOut]);

  const gridTemplateRows = useMemo(() => {
    return `${HEADER_H}px ${rowHeights.map(h => `${h}px`).join(' ')}`;
  }, [rowHeights]);

  const rowTopOffset = (rowIndex) => {
    const prevRowsHeight = rowHeights.slice(0, rowIndex).reduce((a, b) => a + b, 0);
    const gapsBetweenWeeks = rowIndex * GRID_GAP;
    return GRID_PAD + HEADER_H + GRID_GAP + prevRowsHeight + gapsBetweenWeeks;
  };

  // ---------- Formatters ----------
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = toLocalDate(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusText = (item) => {
    // Events don't have status text
    if (item.isEvent) return null;
    
    // Use formatted cr38c_coursestatus field for sessions (readable label)
    if (item.courseStatusName) {
      return item.courseStatusName;
    }
    
    return null;
  };

  const monthNames = [
    'January','February','March','April','May','June','July','August','September','October','November','December'
  ];

  const multiDaySessions = sessions.filter(s => s.isMultiDay).length;
  const singleDaySessions = sessions.filter(s => !s.isMultiDay).length;
  const eventsCount = events.length;

  if (loading) {
    return (
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
          <div style={{ border: '2px solid #e5e7eb', borderTop: '2px solid #3b82f6', borderRadius: '50%', width: '48px', height: '48px', animation: 'spin 1s linear infinite' }}></div>
          <span style={{ marginLeft: '12px', fontSize: '18px', color: '#6b7280' }}>Loading calendar...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Calendar Container */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Header */}
        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </h2>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button onClick={() => navigateMonth(-1)} style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}>
                  <ChevronLeft style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                </button>
                <button onClick={() => navigateMonth(1)} style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}>
                  <ChevronRight style={{ width: '20px', height: '20px', color: '#6b7280' }} />
                </button>
                <button 
                  onClick={goToToday} 
                  style={{ 
                    padding: '6px 12px', 
                    borderRadius: '6px', 
                    border: '1px solid #d1d5db', 
                    backgroundColor: 'white', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginLeft: '8px'
                  }}
                >
                  Today
                </button>
                <input
                  type="date"
                  value={formatDateForInput(currentDate)}
                  onChange={handleDatePickerChange}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    color: '#374151',
                    cursor: 'pointer',
                    marginLeft: '8px'
                  }}
                />
              </div>
            </div>
            
            {userRole === 'editor' && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <a
                  href="https://maritimeinstitute.crm.dynamics.com/main.aspx?appid=2454126a-4f4e-ed11-bba1-0022482a4a4b&forceUCI=1&pagetype=entityrecord&etn=mi_session"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                >
                  Add Course Session
                </a>
                <a
                  href="https://maritimeinstitute.crm.dynamics.com/main.aspx?appid=2454126a-4f4e-ed11-bba1-0022482a4a4b&forceUCI=1&pagetype=entityrecord&etn=cr38c_event"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    textDecoration: 'none',
                    display: 'inline-block'
                  }}
                >
                  Add Event
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div style={{ padding: '24px', paddingTop: '16px' }}>
          {/* Row 1: Campus, Department, Classroom, Resources */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '16px' }}>
            {/* Campus */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Filter by Campus
              </label>
              <select
                value={campusFilter}
                onChange={(e) => setCampusFilter(e.target.value)}
                style={{ display: 'block', width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="all">All Campuses</option>
                {getUniqueCampuses().map(campus => (
                  <option key={campus.value} value={campus.value}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Filter by Department
              </label>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                style={{ display: 'block', width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="all">All Departments</option>
                {getUniqueDepartments().map(department => (
                  <option key={department.value} value={department.value}>
                    {department.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Classroom */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Filter by Classroom
              </label>
              <select
                value={classroomFilter}
                onChange={(e) => setClassroomFilter(e.target.value)}
                style={{ display: 'block', width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="all">All Classrooms</option>
                {getUniqueClassrooms().map(classroom => (
                  <option key={classroom.value} value={classroom.value}>
                    {classroom.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Resources */}
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Filter by Resources
              </label>
              <select
                value={resourcesFilter}
                onChange={(e) => setResourcesFilter(e.target.value)}
                style={{ display: 'block', width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
              >
                <option value="all">All Resources</option>
                {getUniqueResources().map(resource => (
                  <option key={resource.value} value={resource.value}>
                    {resource.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Location, Instructor */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', marginBottom: '16px' }}>
            {/* Location */}
            <div ref={locationDropdownRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Filter by Location
                </label>
                {locationFilter.length > 0 && (
                  <button
                    onClick={() => setLocationFilter([])}
                    style={{
                      padding: '2px 8px',
                      fontSize: '12px',
                      color: '#3b82f6',
                      backgroundColor: 'transparent',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear ({locationFilter.length})
                  </button>
                )}
              </div>
              <button
                onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{locationFilter.length === 0 ? 'All Locations' : `${locationFilter.length} selected`}</span>
                <ChevronDown style={{ width: '16px', height: '16px' }} />
              </button>
              {locationDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 100,
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  {getUniqueLocations().map(location => (
                    <label
                      key={location.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <input
                        type="checkbox"
                        checked={locationFilter.includes(location.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setLocationFilter([...locationFilter, location.value]);
                          } else {
                            setLocationFilter(locationFilter.filter(v => v !== location.value));
                          }
                        }}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      {location.name}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Instructor */}
            <div ref={instructorDropdownRef} style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                  Filter by Instructor
                </label>
                {instructorFilter.length > 0 && (
                  <button
                    onClick={() => setInstructorFilter([])}
                    style={{
                      padding: '2px 8px',
                      fontSize: '12px',
                      color: '#3b82f6',
                      backgroundColor: 'transparent',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Clear ({instructorFilter.length})
                  </button>
                )}
              </div>
              <button
                onClick={() => setInstructorDropdownOpen(!instructorDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{instructorFilter.length === 0 ? 'All Instructors' : `${instructorFilter.length} selected`}</span>
                <ChevronDown style={{ width: '16px', height: '16px' }} />
              </button>
              {instructorDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  marginTop: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 100,
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  {getUniqueInstructors().map(instructor => (
                    <label
                      key={instructor.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <input
                        type="checkbox"
                        checked={instructorFilter.includes(instructor.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setInstructorFilter([...instructorFilter, instructor.value]);
                          } else {
                            setInstructorFilter(instructorFilter.filter(v => v !== instructor.value));
                          }
                        }}
                        style={{ marginRight: '8px', cursor: 'pointer' }}
                      />
                      {instructor.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Calendar Grid Container */}
          <div style={{ position: 'relative' }}>
            {/* Calendar Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gridTemplateRows: gridTemplateRows,
              gap: `${GRID_GAP}px`,
              backgroundColor: '#e5e7eb',
              padding: `${GRID_PAD}px`,
              borderRadius: '8px'
            }}>
              {/* Day Headers */}
              {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(day => (
                <div key={day} style={{ backgroundColor: '#f9fafb', padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#374151', borderRadius: '4px', height: `${HEADER_H}px` }}>
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {calendarData.map((day, index) => {
                const isToday = day.fullDate.toDateString() === new Date().toDateString();
                return (
                  <div
                    key={index}
                    style={{
                      backgroundColor: day.isCurrentMonth ? 'white' : '#f9fafb',
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      border: isToday ? '2px solid #3b82f6' : 'none',
                      color: day.isCurrentMonth ? '#111827' : '#9ca3af'
                    }}
                  >
                    <span style={{ fontWeight: '500', color: isToday ? '#3b82f6' : 'inherit' }}>
                      {day.date}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Event Spans Overlay */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', padding: `${GRID_PAD}px` }}>
              {laidOut.spans.map((span, index) => {
                const laneTop = rowTopOffset(span.row) + ROW_INSET + (span.lane * (EVENT_H + EVENT_GAP));
                const bg = span.session.cr38c_coursecancelled ? '#d1d5db' : 
                          (span.session.courseStatusName && String(span.session.courseStatusName).toLowerCase().includes('tentative')) ? '#86efac' : // Light green for tentative
                          getDepartmentColor(span.session); // Department-based colors
                const radius =
                  span.isFirstSegment && span.isLastSegment ? '4px' :
                  span.isFirstSegment ? '4px 0 0 4px' :
                  span.isLastSegment ? '0 4px 4px 0' : '0';

                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      top: `${laneTop}px`,
                      left: `${(span.startCol / 7) * 100}%`,
                      width: `${((span.endCol - span.startCol + 1) / 7) * 100}%`,
                      height: `${EVENT_H}px`,
                      zIndex: 10,
                      pointerEvents: 'auto'
                    }}
                  >
                    <div
                      onClick={() => setSelectedSession(span.session)}
                      style={{
                        height: '100%',
                        padding: '0 8px',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: '500',
                        borderRadius: radius,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: bg
                      }}
                      title={span.session.mi_sessionname}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {span.session.cr38c_coursecancelled && 'CANCELLED '}
                        {span.session.courseStatusName && String(span.session.courseStatusName).toLowerCase().includes('tentative') && 'TENTATIVE '}
                        {span.session.mi_sessionname || 'Untitled Session'}
                        {!span.session.isEvent && (span.session.mi_seatstaken !== undefined || span.session.mi_maxcapacity !== undefined) && 
                          ` (${span.session.mi_seatstaken || 0} of ${span.session.mi_maxcapacity || 0})`
                        }
                        {span.session.mi_instructor && ` - ${span.session.mi_instructor}`}
                        {span.session.classroomName && ` - ${span.session.classroomName}`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Debug Information Accordion */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <button
          onClick={() => setShowDebug(!showDebug)}
          style={{
            width: '100%',
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: showDebug ? '1px solid #e5e7eb' : 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            color: '#374151'
          }}
        >
          <span>Debug Information</span>
          {showDebug ? (
            <ChevronUp style={{ width: '20px', height: '20px' }} />
          ) : (
            <ChevronDown style={{ width: '20px', height: '20px' }} />
          )}
        </button>
        
        {showDebug && (
          <div style={{ padding: '24px', fontSize: '14px', backgroundColor: '#f9fafb' }}>
            {/* Authentication Debug Info */}
            <div style={{ marginBottom: '24px' }}>
              <AuthDebugger account={account} isAuthenticated={isAuthenticated} />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <strong>Data Summary:</strong> {sessions.length} sessions ({singleDaySessions} single-day, {multiDaySessions} multi-day) + {eventsCount} events
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Active Filters:</strong>
              <ul style={{ marginTop: '4px', paddingLeft: '16px' }}>
                <li>Campus: {campusFilter === 'all' ? 'All Campuses' : getUniqueCampuses().find(c => c.value === campusFilter)?.name || campusFilter}</li>
                <li>Location: {locationFilter.length === 0 ? 'All Locations' : `${locationFilter.length} selected`}</li>
                <li>Instructor: {instructorFilter.length === 0 ? 'All Instructors' : `${instructorFilter.length} selected`}</li>
                <li>Department: {departmentFilter === 'all' ? 'All Departments' : getUniqueDepartments().find(d => d.value === departmentFilter)?.name || departmentFilter}</li>
                <li>Classroom: {classroomFilter === 'all' ? 'All Classrooms' : getUniqueClassrooms().find(c => c.value === classroomFilter)?.name || classroomFilter}</li>
                <li>Resources: {resourcesFilter === 'all' ? 'All Resources' : getUniqueResources().find(r => r.value === resourcesFilter)?.name || resourcesFilter}</li>
              </ul>
            </div>
            
            <div style={{ marginBottom: '16px' }}>
              <strong>Filtered Results:</strong> {filteredSessions.length} sessions displayed
            </div>
            
            {sessions.length > 0 && (
              <div style={{ fontFamily: 'monospace', fontSize: '12px', backgroundColor: '#f3f4f6', padding: '12px', borderRadius: '4px' }}>
                <div><strong>Sample Session Data:</strong></div>
                <div>Name: {sessions[0]?.mi_sessionname || 'N/A'}</div>
                <div>Start: {sessions[0]?.mi_startdate || 'N/A'}</div>
                <div>End: {sessions[0]?.mi_enddate || 'N/A'}</div>
                <div>Location: {sessions[0]?.locationName || sessions[0]?.mi_location || 'N/A'}</div>
                <div>Instructors: {sessions[0]?.instructors?.length || 0}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0, 0, 0, 0.5)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          padding: '16px', 
          zIndex: 50,
          overflow: 'auto'
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', 
            maxWidth: 'min(800px, 90vw)', 
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: 'auto'
          }}>
            <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                    {selectedSession.mi_sessionname}
                  </h3>
                  {userRole === 'editor' && (selectedSession.mi_sessionid || selectedSession.cr38c_eventid) && (
                    <a
                      href={selectedSession.isEvent 
                        ? `https://maritimeinstitute.crm.dynamics.com/main.aspx?appid=2454126a-4f4e-ed11-bba1-0022482a4a4b&forceUCI=1&pagetype=entityrecord&etn=cr38c_event&id=${selectedSession.cr38c_eventid}`
                        : `https://maritimeinstitute.crm.dynamics.com/main.aspx?appid=2454126a-4f4e-ed11-bba1-0022482a4a4b&forceUCI=1&pagetype=entityrecord&etn=mi_session&id=${selectedSession.mi_sessionid}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#6b7280', 
                        padding: '4px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none'
                      }}
                      title={selectedSession.isEvent ? "Edit Event in PowerApps" : "Edit Session in PowerApps"}
                    >
                      <Edit style={{ width: '16px', height: '16px' }} />
                    </a>
                  )}
                </div>
                <button onClick={() => setSelectedSession(null)} style={{ color: '#9ca3af', fontSize: '20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}>
                  ×
                </button>
              </div>
              <div style={{ paddingTop: '8px' }}>
                {getStatusText(selectedSession) && (
                  <p style={{ 
                    fontSize: '18px', 
                    color: String(getStatusText(selectedSession)).toLowerCase().includes('tentative') ? '#dc2626' : '#16a34a',
                    fontWeight: '700',
                    marginBottom: '8px',
                    textTransform: 'uppercase'
                  }}>
                    {getStatusText(selectedSession)}
                  </p>
                )}
                {(selectedSession.mi_sessionid || selectedSession.cr38c_eventid) && (
                  <p style={{ fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>
                    ID: {selectedSession.isEvent ? selectedSession.cr38c_eventid : selectedSession.mi_sessionid}
                  </p>
                )}
              </div>
            </div>

            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>Date</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalendarIcon style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                  <div style={{ color: '#6b7280', fontSize: '14px' }}>
                    <div>
                      {formatDate(selectedSession.mi_startdate)}
                      {selectedSession.mi_enddate && formatDate(selectedSession.mi_startdate) !== formatDate(selectedSession.mi_enddate) && 
                        ` - ${formatDate(selectedSession.mi_enddate)}`
                      }
                    </div>
                  </div>
                </div>
              </div>

              {(selectedSession.locationName || selectedSession.mi_location) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>Location</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MapPin style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                    <span style={{ color: '#6b7280', fontSize: '14px' }}>
                      {selectedSession.locationName || selectedSession.mi_location || 'Location TBD'}
                    </span>
                  </div>
                </div>
              )}

              {(selectedSession.instructors?.length > 0 || selectedSession.mi_instructor) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>
                    {selectedSession.instructors?.length > 1 ? 'Instructors' : 'Instructor'}
                  </h4>
                  {selectedSession.instructors?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {selectedSession.instructors.map((instructor, index) => (
                        <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <User style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                            <span style={{ color: '#111827', fontSize: '14px' }}>
                              {instructor.name}
                              {instructor.isPrimary && (
                                <span style={{ 
                                  marginLeft: '8px', 
                                  padding: '2px 6px', 
                                  backgroundColor: '#3b82f6', 
                                  color: 'white', 
                                  fontSize: '12px', 
                                  borderRadius: '4px',
                                  fontWeight: '500'
                                }}>
                                  Primary
                                </span>
                              )}
                              {instructor.role && (
                                <span style={{ 
                                  marginLeft: '8px', 
                                  padding: '2px 6px', 
                                  backgroundColor: 
                                    String(instructor.role).toLowerCase().includes('support') || String(instructor.role).toLowerCase().includes('safety') ? '#f97316' : // Orange
                                    String(instructor.role).toLowerCase().includes('course') ? '#15803d' : // Dark green
                                    String(instructor.role).toLowerCase().includes('under') ? '#86efac' : // Light green
                                    '#6b7280', // Default gray
                                  color: 'white', 
                                  fontSize: '12px', 
                                  borderRadius: '4px',
                                  fontWeight: '500'
                                }}>
                                  {instructor.role}
                                </span>
                              )}
                            </span>
                          </div>
                          {(instructor.startDate || instructor.endDate) && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginLeft: '24px' }}>
                              {instructor.startDate && formatDate(instructor.startDate)} - {instructor.endDate && formatDate(instructor.endDate)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                      <span style={{ color: '#111827', fontSize: '14px' }}>{selectedSession.mi_instructor}</span>
                    </div>
                  )}
                </div>
              )}

              {!selectedSession.isEvent && (selectedSession.mi_seatstaken !== undefined || selectedSession.mi_maxcapacity !== undefined) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>Enrollment</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <User style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                      <span style={{ color: '#6b7280', fontSize: '14px' }}>
                        {selectedSession.mi_seatstaken || 0} of {selectedSession.mi_maxcapacity || 0} seats (confirmed students)
                      </span>
                    </div>
                    {userRole === 'editor' && selectedSession.placeholderCount > 0 && (
                      <div style={{ marginLeft: '24px', fontSize: '13px', color: '#9ca3af' }}>
                        <div>Placeholders: {selectedSession.placeholderCount}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(selectedSession.departmentName || selectedSession.cr38c_department) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>Department</h4>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    {selectedSession.departmentName || selectedSession.cr38c_department}
                  </p>
                </div>
              )}



              {(selectedSession.classroomName || selectedSession.mi_classroom) && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>Classroom</h4>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>
                    {selectedSession.classroomName || selectedSession.mi_classroom}
                  </p>
                </div>
              )}

              {selectedSession.mi_notes && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>
                    {selectedSession.isEvent ? 'Event Notes' : 'Notes'}
                  </h4>
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>{selectedSession.mi_notes}</p>
                </div>
              )}

              {!selectedSession.isEvent && selectedSession.resources && selectedSession.resources.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>
                    {selectedSession.resources.length > 1 ? 'Resources' : 'Resource'}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {selectedSession.resources.map((resource, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#6b7280', fontSize: '14px' }}>• {resource.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Event-specific fields (only show for events) */}
              {selectedSession.isEvent && (
                <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '16px' }}>Event Information</h4>
                  
                  {(selectedSession.campusName || selectedSession.mi_billingresponsibility) && (
                    <div style={{ marginBottom: '16px' }}>
                      <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>Campus</h4>
                      <p style={{ color: '#6b7280', fontSize: '14px' }}>
                        {selectedSession.campusName || selectedSession.mi_billingresponsibility}
                      </p>
                    </div>
                  )}
                  
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '4px' }}>Event Times</h4>
                    <div style={{ color: '#6b7280', fontSize: '14px' }}>
                      <div><strong>Start:</strong> {formatDate(selectedSession.mi_startdate)} at {formatTime(selectedSession.mi_startdate)}</div>
                      <div><strong>End:</strong> {formatDate(selectedSession.mi_enddate)} at {formatTime(selectedSession.mi_enddate)}</div>
                    </div>
                  </div>
                </div>
              )}

              {selectedSession.childSessions && selectedSession.childSessions.length > 0 && (
                <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '16px' }}>Bundle Sessions</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                    {selectedSession.childSessions.map((child, index) => (
                      <div key={index} style={{ 
                        backgroundColor: '#f9fafb', 
                        padding: '16px', 
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <h5 style={{ fontWeight: '600', color: '#111827', fontSize: '14px', margin: 0 }}>
                            {child.mi_sessionname || 'Untitled Session'}
                          </h5>
                          {userRole === 'editor' && child.mi_sessionid && (
                            <a
                              href={`https://maritimeinstitute.crm.dynamics.com/main.aspx?appid=2454126a-4f4e-ed11-bba1-0022482a4a4b&forceUCI=1&pagetype=entityrecord&etn=mi_session&id=${child.mi_sessionid}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#6b7280', 
                                padding: '2px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                textDecoration: 'none'
                              }}
                              title="Edit Session in PowerApps"
                            >
                              <Edit style={{ width: '12px', height: '12px' }} />
                            </a>
                          )}
                        </div>
                        
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                          <strong>Date & Time:</strong>
                          <div>{formatDate(child.mi_startdate)}</div>
                          <div>{formatTime(child.mi_startdate)} - {formatTime(child.mi_enddate)}</div>
                        </div>
                        
                        {(child.mi_seatstaken !== null || child.mi_maxcapacity !== null) && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                            <strong>Enrollment:</strong> {child.mi_seatstaken || 0}/{child.mi_maxcapacity || 'N/A'} (confirmed)
                            {userRole === 'editor' && child.placeholderCount > 0 && (
                              <div style={{ marginLeft: '8px', fontSize: '11px', color: '#9ca3af' }}>
                                <span>Placeholders: {child.placeholderCount}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {child.instructors && child.instructors.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                            <strong>Instructor(s):</strong>
                            {child.instructors.map((instructor, idx) => (
                              <div key={idx} style={{ marginBottom: '4px' }}>
                                <div>
                                  {instructor.name}
                                  {instructor.role && <span style={{ color: '#6b7280' }}> ({instructor.role})</span>}
                                  {instructor.isPrimary && <span style={{ color: '#3b82f6', fontWeight: '500' }}> - Primary</span>}
                                </div>
                                {(instructor.startDate || instructor.endDate) && (
                                  <div style={{ fontSize: '11px', color: '#9ca3af', marginLeft: '16px' }}>
                                    {instructor.startDate && formatDate(instructor.startDate)} - {instructor.endDate && formatDate(instructor.endDate)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {child.resources && child.resources.length > 0 && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                            <strong>Resources:</strong>
                            {child.resources.map((resource, ridx) => (
                              <div key={ridx}>• {resource.name}</div>
                            ))}
                          </div>
                        )}
                        
                        {child.mi_notes && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            <strong>Notes:</strong>
                            <div>{child.mi_notes}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ padding: '24px', borderTop: '1px solid #e5e7eb' }}>
              <button onClick={() => setSelectedSession(null)} style={{ width: '100%', backgroundColor: '#3b82f6', color: 'white', padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calendar;
